import { useState, useEffect, useRef, useCallback } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'

import ScrollOverflowText from 'components/ScrollOverflowText'

const SpeakerIcon = ({ muted }) => (
    <svg 
        width="16" 
        height="16" 
        viewBox="0 0 24 24" 
        fill="currentColor" 
        className="w-4 h-4"
    >
        {muted ? (
            // Speaker with slash (muted)
            <>
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </>
        ) : (
            // Speaker (unmuted)
            <>
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </>
        )}
    </svg>
)

export default function ExerciseVideo({ exercise }) {

    var videoDisplayMode = exercise.videoDisplayMode
    // var videoDisplayMode = 'portrait'
    // var videoDisplayMode = 'landscape'

    const videoRef = useRef(null)

    const [videoMetadataLoaded, setVideoMetadataLoaded] = useState(false)
    const [videoLoaded, setVideoLoaded] = useState(false)
    const [videoPlaying, setVideoPlaying] = useState(false)
    const [videoMuted, setVideoMuted] = useState(true)

    const HandleLoadedMetadata = useCallback(() => {
        setVideoMetadataLoaded(true)
        
        if (videoRef.current) {
            videoRef.current.play().catch(e => {
                console.warn('Video auto-play failed on metadata load:', e)
            })
        }
    }, [])
    
    const HandleLoadedData = useCallback(() => {
        setVideoLoaded(true)

        if (videoRef.current) {
            videoRef.current.play().catch(e => {
                console.warn('Video auto-play failed:', e)
            })
        }
    }, [])

    const ToggleMute = () => { 
        setVideoMuted(!videoMuted)
    }

    useEffect(() => { videoRef.current.load() }, [])

    return (
        <div className='w-full flex-grow rounded-lg overflow-hidden relative bg-black z-100'>
            {/* Background blur effect for landscape videos */}
            {/* blur brightness-75 scale-110 are expensive styles. So only apply on active card */}
            {videoDisplayMode === 'landscape' && exercise.imageUrl && (
                <div className="absolute inset-0 z-0 opacity-50">
                    <img 
                        src={exercise.imageUrl} 
                        alt={`${exercise.name} background`} 
                        className="w-full h-full object-cover blur-sm scale-110 " 
                        style={{ 
                            willChange: 'filter, transform',
                            backfaceVisibility: 'hidden',
                            transform: 'translateZ(0)'
                        }}
                    />
                </div>
            )}

            {/* Top overlay with exercise name and info button */}
            <div 
                className='absolute top-0 left-0 w-full p-2 z-20'
                style={{ transform: 'translateZ(0)' }} // Prevent pop effect on overlay items
            >
                <div className='flex items-center gap-x-1'>
                    <div className="py-1 px-2 bg-gray-700/60 rounded-lg overflow-hidden">
                        <ScrollOverflowText 
                            text={exercise.name}
                            className="text-white font-semibold text-xs"
                        />
                    </div>

                    <div className='flex-1' />

                    <button
                        className='p-1 bg-gray-700/60 text-white rounded-lg'
                        title="Exercise Information"
                    >
                        <InformationCircleIcon className='w-4 h-4' />
                    </button>
                </div>
            </div>

            {/* Phase and set number with audio toggle */}
            <div 
                className='absolute bottom-0 left-0 w-full p-2 z-20'
                style={{ transform: 'translateZ(0)' }} // Prevent pop effect on overlay items
            >
                <div className='flex items-center'>
                    <div className='py-1 px-2 bg-gray-700/60 text-white font-semibold text-xs rounded-lg'>Phase Name</div>
                    <div className='flex-1' />
                    <div className='py-1 px-2 bg-gray-700/60 text-white font-semibold text-xs rounded-lg'>Round 1 of 2</div>
                    
                    <button
                        onClick={(e) => {
                            e.stopPropagation() // Prevent triggering the modal
                            ToggleMute()
                        }}
                        className='ml-2 p-1 bg-gray-700/60 text-white rounded-lg'
                        title={videoMuted ? 'Unmute' : 'Mute'}
                    >
                        <SpeakerIcon muted={videoMuted} />
                    </button>
                </div>
            </div>

            {/* Video element */}
            <div 
                className={`
                    relative h-full w-full
                    ${videoDisplayMode === 'landscape' ? 'flex items-center justify-center' : ''}
                `}
            >
                <video
                    ref={videoRef}
                    className={`
                        pointer-events-none 
                        ${videoDisplayMode === 'landscape' ? 'max-h-full max-w-full object-contain' : 'h-full w-full object-cover'}
                    `}
                    loop
                    muted={videoMuted}
                    playsInline
                    webkit-playsinline="true"
                    x-webkit-airplay="deny"
                    preload="none"
                    tabIndex={-1}
                    disablePictureInPicture
                    controls={false}
                    width="720" 
                    height="400"
                    onLoadedMetadata={() => HandleLoadedMetadata()} // Required for iOS autoplay
                    onLoadedData={() => HandleLoadedData()}
                    onPlaying={() => setVideoPlaying(true)}
                >
                    <source src={exercise.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            </div>

            {/* Thumbnail overlay */}
            <img 
                src={exercise.imageUrl} 
                alt={`${exercise.name} thumbnail`} 
                className={`
                    absolute inset-0 w-full h-full object-cover pointer-events-none z-5
                    ${(videoMetadataLoaded && videoLoaded && videoPlaying) ? 'transition-opacity duration-1000 opacity-0' : 'opacity-100'}
                `}
            />
        </div>
    )
}